// The Registrar the session is working on, chosen from the main menu when there
// is a choice.
//
// per docs/shape-ops-tenant-selector.md §6 Revision 2026-10-12 and §6.2 (the
// label, 2026-10-13), §9 (the selection is the server's, 2026-10-13). Register
// entry UC11-05 (CF-27): a user attached to several Registrars selects the
// active one and can change it at any time from the main menu. The owner ruling
// of 2026-09-09 had taken the control out of the account trigger, because a
// select cannot live inside a button, and left no way to switch; it now lives in
// the rail beside the account, not inside it.
import { useTranslate } from '@tolgee/react';
import { MudButton, MudIcon, MudSelect } from '@egov-moldova/mud-react';
import { isReporter } from '../../app/access-model';
import { useActiveRegistrar } from '../../hooks/use-active-registrar';
import { useSession } from '../../hooks/use-session';
import './registrar-selector.css';

// Declared here rather than imported: the house pattern, same as
// StockReportingPage.tsx:40. `selectOptions` is rendered as `<option>` children
// of `<MudSelect>`, which reads them from the light DOM.
interface SelectOption {
  value: string;
  label: string;
}

interface SelectChangeDetail {
  value: string;
}

/**
 * How one Registrar reads in the select: its IDNO, then its name.
 *
 * TC-UC11-05-01 wants both on screen ("denumire + IDNO"). AGE's select clips the
 * trigger text and each option to one line with an ellipsis, and the rail leaves
 * it about 190px, so something is cut whenever a name is long. The cut falls on
 * the end of the string, and the IDNO is the part that tells two similar names
 * apart, so the IDNO leads: it is thirteen digits, always fits, and what the
 * ellipsis takes is the tail of a name. An IDNO the catalog could not resolve is
 * already the name, and is printed once.
 */
export function registrarOptionLabel(idno: string, name: string): string {
  return name === idno ? idno : `${idno} · ${name}`;
}

export interface RegistrarSelectorProps {
  /** The 72px icon rail, where a select does not fit. */
  collapsed: boolean;
  /** Opens the rail again; the collapsed control is the way back to the select. */
  onExpand: () => void;
}

/**
 * Drawn for a Registrar Administrator or Operator whose scope names two or more
 * Registrars, and for nobody else: one Registrar is not a question, and the
 * System Administrator and the observers hold none (register, "Selectare
 * Registrator activ"). The scope is the active role's own.
 */
export function RegistrarSelector({
  collapsed,
  onExpand,
}: RegistrarSelectorProps): JSX.Element | null {
  const { t } = useTranslate();
  const session = useSession().data;
  const { idno, name, options, hasChoice, choose, choosing, chooseErrorKey } = useActiveRegistrar();

  if (!session || !isReporter(session.activeRole) || !hasChoice) return null;

  if (collapsed) {
    return (
      <div className="simsm-registrar-selector simsm-registrar-selector--collapsed">
        <MudButton
          shape="circular"
          variant="strict"
          appearance="text"
          size="sm"
          iconOnly
          label={t('registrar.selector.collapsed', { name })}
          title={name}
          onClick={onExpand}
        >
          <MudIcon slot="icon" name="building" size={20} />
        </MudButton>
      </div>
    );
  }

  const selectOptions: SelectOption[] = options.map((option) => ({
    value: option.idno,
    label: registrarOptionLabel(option.idno, option.name),
  }));
  // The closed select clips to one line. The line under it is the select's own
  // helper text, which wraps, so the Registrar being worked on can be read whole.
  const active = selectOptions.find((option) => option.value === idno);
  // While the server is asked the select shows the one picked and takes no other
  // pick; a refusal puts it back on the Registrar in force and says why.
  const refusal = chooseErrorKey ? { invalid: true, errorText: t(chooseErrorKey) } : {};

  return (
    <div className="simsm-registrar-selector">
      <MudSelect
        size="medium"
        label={t('registrar.selector.label')}
        value={choosing ?? idno}
        placeholder={t('registrar.selector.placeholder')}
        helperText={active?.label ?? ''}
        disabled={choosing !== null}
        {...refusal}
        onMudChange={(event: CustomEvent<SelectChangeDetail>) => choose(event.detail.value)}
      >
        {selectOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </MudSelect>
    </div>
  );
}

/**
 * The entity named on the step that commits.
 *
 * `core.stock` is append-only: a confirmed report cannot be re-filed and an
 * approved correction is another row under the same key. So the dialogue that
 * takes the irreversible act says who it is being taken for, rather than
 * leaving it to a header in the corner of a screen the reader stopped looking
 * at (owner ruling 2026-09-06, point 4).
 *
 * Silent for a session with one Registrar, deliberately: naming the only
 * entity an act could possibly have been filed under is noise, and the sentence
 * exists to answer "which of the two".
 */
export function ActiveRegistrarNote(): JSX.Element | null {
  const { t } = useTranslate();
  const { name, hasChoice } = useActiveRegistrar();

  if (!hasChoice || !name) return null;

  return (
    <p className="simsm-registrar-note">
      <span className="simsm-registrar-note__label">{t('registrar.active.note.label')}</span>{' '}
      <span className="simsm-registrar-note__name">{name}</span>
    </p>
  );
}
